import { formatDistanceToNow, isValid } from 'date-fns'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'
import { parseUTCDate, formatDateTime } from '@/lib/utils'

interface RelativeTimeProps {
  date: string | Date | null | undefined
  className?: string
}

/**
 * Displays a relative timestamp (e.g., "5 minutes ago") with the full date/time
 * shown in a tooltip on hover.
 *
 * Features:
 * - Uses date-fns formatDistanceToNow() for relative display
 * - Shows full date/time in tooltip (via formatDateTime)
 * - Handles null, undefined, and invalid dates gracefully
 * - Keyboard accessible (tooltip appears on focus)
 *
 * @example
 * <RelativeTime date={design.created_at} />
 * <RelativeTime date={task.started_at} className="text-xs" />
 */
export function RelativeTime({ date, className }: RelativeTimeProps) {
  if (!date) {
    return <span className={className}>—</span>
  }

  const parsedDate = parseUTCDate(date)

  if (!parsedDate || !isValid(parsedDate)) {
    return <span className={className}>—</span>
  }

  const relativeTime = formatDistanceToNow(parsedDate, { addSuffix: true })
  const fullDateTime = formatDateTime(date)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={className} tabIndex={0}>
          {relativeTime}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <span>{fullDateTime}</span>
      </TooltipContent>
    </Tooltip>
  )
}
